import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDeepCompareEffectNoCheck } from "use-deep-compare-effect";

import { LocalStorageConstants } from "../../../../constants";
import { parseProjectPhase, ProjectPhase } from "../../../../constants/project-phase";
import { ProjectDates, ProjectDatesData, SelectData } from "../../../../models";
import { searchProjectTracker, searchWorkflowTasks } from "../../../../service/query";
import { ServiceError, Status } from "../../../../service/Shared";
import { useAuth } from "../../../../useAuth";
import { jsonReviver, safeLocalStorage, useCustomEventListener } from "../../../../utils";
import { getProjectRoadmapRoute } from "../../../../utils/routes";
import { dataGridMapFilterCriteria, Toast } from "../../../../widget";

interface UseProjectRoadmapPageReturnData {
  errors: ServiceError[] | undefined;
  data: ProjectDatesData[] | undefined;
  projectsMultiSelectData: SelectData | undefined;
  editDatesPanelData: ProjectDatesData | undefined;
  setEditDatesPanelData: Dispatch<SetStateAction<ProjectDatesData | undefined>>;
  roadmapProjectUuids: string[];
  setRoadmapProjectUuids: Dispatch<SetStateAction<string[]>>;
  showAddProjectsModal: boolean;
  setShowAddProjectsModal: Dispatch<SetStateAction<boolean>>;
  closeSelectProjectsModal: () => void;
  showEditDatesPanel: boolean;
  closeEditDatesPanel: () => void;
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  onSaveProjectsSelectData: () => void;
  fetchSelectData: (displayName?: string) => Promise<void>;
}

export const useProjectRoadmapPage = (): UseProjectRoadmapPageReturnData => {
  const { user, currentUserType } = useAuth();
  const projectRoadmapLocalStorageItemName = `${user?.userUuid ? `${user?.userUuid}_` : ""}${LocalStorageConstants.PROJECT_ROADMAP_PREFERENCES}`;

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [errors, setErrors] = useState<ServiceError[]>();
  const [isLoading, setIsLoading] = useState(true);

  const [data, setData] = useState<ProjectDatesData[]>([]);
  const [projectsMultiSelectData, setProjectsMultiSelectData] = useState<SelectData>();

  const [roadmapProjectUuids, setRoadmapProjectUuids] = useState<string[]>([]);

  const [editDatesPanelData, setEditDatesPanelData] = useState<ProjectDatesData>();

  const [showEditDatesPanel, setShowEditDatesPanel] = useState(false);
  const [showAddProjectsModal, setShowAddProjectsModal] = useState(false);

  const closeEditDatesPanel = (): void => {
    setShowEditDatesPanel(false);
    navigate(getProjectRoadmapRoute(currentUserType), { replace: true });
  };

  const updateProjectRoadmapLocalStorage = (): void => {
    try {
      safeLocalStorage.setItem(projectRoadmapLocalStorageItemName, JSON.stringify(roadmapProjectUuids));
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err) {
      const message = err instanceof Error ? err.message : "An unknown error occurred while saving to local storage.";
      Toast.warn({ message });
    }
  };

  const fetchSelectData = useCallback(async (displayName?: string) => {
    const filterCriteria = dataGridMapFilterCriteria([]);

    filterCriteria.dates = {
      operator: "neq",
      value: "{}",
    };

    filterCriteria.workflow = {
      uuid: {
        operator: "neq",
        value: null,
      },
    };

    if (displayName) {
      filterCriteria.displayName = {
        operator: "startsWith",
        value: displayName,
      };
    }

    const res = await searchProjectTracker({
      paging: {
        limit: 50,
        beforeCursor: null,
        afterCursor: null,
      },
      filter: { results: filterCriteria },
    });
    if (res.status !== Status.Success || res.data?.results !== undefined) {
      setProjectsMultiSelectData(
        res.data?.results.map((project) => ({
          key: project.uuid,
          value: project.displayName,
        })) ?? []
      );
    }
  }, []);

  const fetchRoadmapData = useCallback(
    async (uuids: string[]) => {
      setIsLoading(true);
      const projectTrackerFilterCriteria = dataGridMapFilterCriteria([]);

      projectTrackerFilterCriteria.uuid = {
        operator: "in",
        value: uuids,
      };

      projectTrackerFilterCriteria.workflow = {
        uuid: {
          operator: "neq",
          value: null,
        },
      };

      const res = await searchProjectTracker({
        paging: {
          limit: 15,
          beforeCursor: null,
          afterCursor: null,
        },
        filter: { results: projectTrackerFilterCriteria },
      });
      if (res.status !== Status.Success || res.data?.results !== undefined) {
        const milestonesFilterCriteria = dataGridMapFilterCriteria([]);

        milestonesFilterCriteria.deadlineDate = {
          operator: "neq",
          value: null,
        };

        milestonesFilterCriteria.isMilestone = {
          operator: "eq",
          value: true,
        };

        milestonesFilterCriteria.workflowObjectUuid = {
          operator: "in",
          value: uuids,
        };

        const milestonesRes = await searchWorkflowTasks({
          paging: {
            limit: 1000,
            beforeCursor: null,
            afterCursor: null,
          },
          filter: {
            results: milestonesFilterCriteria,
          },
        });

        if (milestonesRes.status === Status.Success && milestonesRes.data?.results !== undefined) {
          setData(
            res.data?.results.map((project) => {
              const parsedJsonDates = JSON.parse(project.dates, jsonReviver) as ProjectDates;

              return {
                uuid: project.uuid,
                displayName: project.displayName,
                rowVersion: project.rowVersion,
                currentStatus: project.status,
                cachedPhase: parseProjectPhase(project.cachedPhase) ?? ProjectPhase.feasibility,
                milestones: milestonesRes.data?.results
                  .filter((milestone) => milestone.objectUuid === project.uuid)
                  .map((milestone) => milestone),
                dates: {
                  ...parsedJsonDates,
                  ongoingMonitoring: {
                    ...parsedJsonDates.ongoingMonitoring,
                    cycles: parsedJsonDates.ongoingMonitoring?.cycles?.sort((a, b) => a.sequence - b.sequence) ?? [],
                  },
                } as ProjectDates,
                standardSpecificSchemaUuid: project.standardSpecificSchemaUuid,
              };
            }) ?? []
          );
          if (res.data && res.data?.results.some((project) => !uuids.includes(project.uuid))) {
            setRoadmapProjectUuids(() => res.data?.results.map((project) => project.uuid) ?? []);

            updateProjectRoadmapLocalStorage();
          }
        }
        if (res.data?.results.length === 0) {
          setIsLoading(false);
        }
      }
    },
    [roadmapProjectUuids]
  );

  const onSaveProjectsSelectData = (): void => {
    setErrors([]);
    if (roadmapProjectUuids && roadmapProjectUuids?.length > 15) {
      setErrors([{ code: "ERROR_15_PROJECTS_MAXIMUM", message: "Please select no more than 15 projects" }]);
      return;
    }

    updateProjectRoadmapLocalStorage();
    if (roadmapProjectUuids) fetchRoadmapData(roadmapProjectUuids);

    setShowAddProjectsModal(false);
  };

  // Get project uuids from local storage if they exist
  const fetchAndSetRoadmapProjectUuids = (): void => {
    const localStorageProjectsString = safeLocalStorage.getItem(projectRoadmapLocalStorageItemName);
    if (localStorageProjectsString) {
      const localStorageProjectsObject = JSON.parse(localStorageProjectsString);
      setRoadmapProjectUuids(localStorageProjectsObject);
      if (localStorageProjectsObject) {
        fetchRoadmapData(localStorageProjectsObject);
      }
    } else {
      setIsLoading(false);
    }
  };

  const closeSelectProjectsModal = (): void => {
    setShowAddProjectsModal(false);
    fetchAndSetRoadmapProjectUuids();
  };

  // Listen to the custom pushState change event we fire when the user selects EditDates & keep react-router in sync
  useCustomEventListener("pushState", () => {
    navigate(window.location.pathname + window.location.search, { replace: true });
  });

  useDeepCompareEffectNoCheck(() => {
    const projectUuid = searchParams.get("projectUuid");
    if (projectUuid) {
      setEditDatesPanelData(data.filter((project) => project.uuid === projectUuid)[0]);
      setShowEditDatesPanel(true);
    }
  }, [searchParams, data]);

  useDeepCompareEffectNoCheck(() => {
    if (editDatesPanelData) {
      setData((projectRoadmapData) => {
        return projectRoadmapData.map((project) => {
          if (project.uuid === editDatesPanelData.uuid && project.rowVersion !== editDatesPanelData.rowVersion) {
            return {
              ...project,
              dates: editDatesPanelData.dates,
              milestones: editDatesPanelData.milestones,
              rowVersion: editDatesPanelData.rowVersion,
            };
          }
          return project;
        });
      });
    }
  }, [editDatesPanelData]);

  useEffect(() => {
    fetchAndSetRoadmapProjectUuids();
    fetchSelectData();
  }, []);

  return {
    errors,
    data,
    projectsMultiSelectData,

    editDatesPanelData,
    setEditDatesPanelData,
    showEditDatesPanel,
    closeEditDatesPanel,

    roadmapProjectUuids,
    setRoadmapProjectUuids,

    showAddProjectsModal,
    setShowAddProjectsModal,
    closeSelectProjectsModal,

    isLoading,
    setIsLoading,

    onSaveProjectsSelectData,
    fetchSelectData,
  };
};
